import { useCallback, useEffect, RefObject, KeyboardEvent } from 'react';
import { usePopoverContext } from '@redsift/popovers';
import { UseFocusWithinGroupProps, useFocusOnListItem } from '@redsift/design-system';

export function useMenuItem(props: {
  domElementRef: RefObject<Element>;
  id: string;
  isDisabled?: boolean;
  onClick?: () => void;
  isLinkMenuItem?: boolean;
  linkRef?: RefObject<HTMLAnchorElement>;
  hasPopup?: boolean;
}): UseFocusWithinGroupProps & {
  isDisabled?: boolean;
} {
  const { domElementRef, id, isDisabled, onClick, isLinkMenuItem, linkRef, hasPopup } = props;

  const { handleOpen, hideInsteadOfClose, setHideInsteadOfClose } = usePopoverContext();
  const {
    tabIndex,
    isFocused,
    handleKeyDown: handleFocusKeyDown,
    handleClick: handleFocusClick,
  } = useFocusOnListItem({ domElementRef, isDisabled: isDisabled!, id });

  useEffect(() => {
    if (hasPopup && !hideInsteadOfClose) {
      setHideInsteadOfClose?.(true);
    }
  }, [hasPopup, hideInsteadOfClose, setHideInsteadOfClose]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const code = event.code;
    if (code === 'Enter' || code === 'Space') {
      if (isLinkMenuItem) {
        linkRef?.current?.click();
      } else {
        onClick?.();
      }
    }
    handleFocusKeyDown(event);
    handleOpen(false);
  }, []);

  const handleClick = useCallback(() => {
    handleFocusClick();
    onClick?.();
    handleOpen(false);
  }, []);

  useEffect(() => {
    if (isFocused && domElementRef.current) {
      (domElementRef as RefObject<SVGElement | HTMLElement>).current?.focus();
    }
  }, [isFocused]);

  return {
    tabIndex,
    isFocused,
    isDisabled,
    handleKeyDown,
    handleClick,
  };
}
